/**
 * The ground mark of a ท่าใหญ่'s tell (render/longMove.ts, the rules' `longTell`): the opener's real area at its real size
 * from the first tell frame to the end of the piece, in the FX pixel rules (1-px dots on the ground, no glow): a field or
 * ground circle, a lunge's lane, a volley's dotted line. A checker fill closes in from the edge as the tell runs (the
 * telegraphs' reading); the foe's side (anyone but the caster) gets a red outer edge.
 */
import { tellsOf } from '../longMove';
import { C } from '../fx/sampleArt/families/swordKit';
import { registerRenderPass } from './registry';

registerRenderPass({
  id: 'longMoveTell',
  layer: 'ground',
  order: 907,
  draw({ g, world: w, iso, ox, oy }) {
    const now = w.time;
    for (const m of tellsOf(w)) {
      const k = Math.max(0, Math.min(1, (now - m.at) / Math.max(1e-6, m.tell)));
      const foeSide = w.player.id !== m.src;
      const dot = (wx: number, wy: number, col: string) => {
        const [x, y] = iso.proj(wx, wy);
        g.fillStyle = col;
        g.fillRect(Math.round(x + ox), Math.round(y + oy), 1, 1);
      };
      g.save();
      if (m.shape === 'field' || m.shape === 'ground') {
        const r = m.radius;
        const n = Math.max(48, Math.round(r * 60));
        for (let i = 0; i < n; i++) {
          const a = (i / n) * Math.PI * 2;
          const c = Math.cos(a);
          const s = Math.sin(a);
          g.globalAlpha = 1;
          dot(m.x + c * r, m.y + s * r, C('jade', i % 3 ? 5 : 6));
          if (foeSide) dot(m.x + c * (r + 0.06), m.y + s * (r + 0.06), C('maroon', 4));
        }
        // The fill: rings of checker dots from the edge in, as far as the tell has run (kept full through the piece).
        g.globalAlpha = 0.5;
        for (let q = 1; q <= 8; q++) {
          const rr = r * (1 - q / 9);
          if (q / 9 > k) break;
          const nn = Math.max(12, Math.round(rr * 30));
          for (let i = q % 2; i < nn; i += 2) {
            const a = (i / nn) * Math.PI * 2;
            dot(m.x + Math.cos(a) * rr, m.y + Math.sin(a) * rr, C('jade', 4));
          }
        }
      } else {
        // A lane (lunge, 1.2 m wide) or a dotted line (volley) to the range along the tell's direction.
        const half = m.shape === 'lunge' ? 0.6 : 0;
        const len = m.range * (m.shape === 'volley' ? 1 : Math.max(0.15, k));
        const px = -m.dirY;
        const py = m.dirX;
        g.globalAlpha = 1;
        for (let d = 0; d <= len; d += 0.08)
          for (const side of half ? [-half, half] : [0]) {
            if (!half && Math.floor(d / 0.16) % 2) continue;
            dot(m.x + m.dirX * d + px * side, m.y + m.dirY * d + py * side, C(foeSide ? 'maroon' : 'jade', 5));
          }
      }
      g.restore();
    }
  },
});
